import { ParsedEntity } from 'interfaces/entity-resolver'

export class EntityNameParser {
  /**
   * Parse entity name into components
   * Examples:
   * - "T000" → { terminalName: "T000", entityType: "terminal" }
   * - "A1" → { terminalName: "T000", ansibleName: "A1", entityType: "ansible" }
   * - "T001A2M1" → { terminalName: "T001", ansibleName: "T001A2", macMiniName: "T001A2M1", entityType: "mac-mini" }
   * - "A0M1" → { terminalName: "T000", macMiniName: "A0M1", entityType: "mac-mini", hasAnsible: false }
   */
  static parse(entityName: string): ParsedEntity {
    // Validate input
    if (!entityName || entityName.trim().length === 0) {
      throw new Error('Entity name cannot be empty')
    }

    // Normalize input
    const normalizedName = entityName.trim().toUpperCase()

    // Validate format - support all entity naming patterns
    const validPatterns = [
      /^T\d{3}$/, // T000, T001
      /^A\d+$/, // A1, A2 (T000 ansibles)
      /^T\d{3}A\d+$/, // T001A1, T001A2 (T001+ ansibles)
      /^A\d+M\d+$/, // A1M1, A0M1 (T000 mac minis)
      /^T\d{3}A\d+M\d+$/, // T001A1M1, T001A0M1 (T001+ mac minis)
      /^A\d+M\d+P\d+$/, // A1M1P1 (T000 phones with ansible+mac)
      /^A0M0P\d+$/, // A0M0P1 (T000 phones direct)
      /^T\d{3}A\d+M\d+P\d+$/, // T001A1M1P1 (T001+ phones with ansible+mac)
      /^T\d{3}A0M0P\d+$/, // T001A0M0P1 (T001+ phones direct)
    ]

    if (!validPatterns.some((pattern) => pattern.test(normalizedName))) {
      throw new Error(`Invalid entity name format: ${entityName}`)
    }

    // Terminal pattern: T000, T001, etc.
    if (/^T\d{3}$/.test(normalizedName)) {
      return {
        terminalName: normalizedName,
        entityType: 'terminal',
        hasAnsible: false,
      }
    }

    // Determine if it starts with terminal prefix
    const hasTerminalPrefix = /^T\d{3}/.test(normalizedName)
    const terminalName = hasTerminalPrefix
      ? normalizedName.substring(0, 4)
      : 'T000'

    // Remove terminal prefix for parsing
    const nameWithoutTerminal = hasTerminalPrefix
      ? normalizedName.substring(4)
      : normalizedName

    // Parse components
    const ansibleMatch = nameWithoutTerminal.match(/^A(\d+)/)
    const macMiniMatch = nameWithoutTerminal.match(/M(\d+)/)
    const phoneMatch = nameWithoutTerminal.match(/P(\d+)/)

    // Build full names
    let ansibleName: string | undefined
    let macMiniName: string | undefined
    let phoneName: string | undefined

    if (ansibleMatch) {
      const ansibleNum = ansibleMatch[1]
      ansibleName = hasTerminalPrefix
        ? `${terminalName}A${ansibleNum}`
        : `A${ansibleNum}`
    }

    if (macMiniMatch) {
      const macMiniNum = macMiniMatch[1]
      if (ansibleMatch) {
        macMiniName = `${ansibleName}M${macMiniNum}`
      } else {
        // A0 pattern (no ansible)
        macMiniName = hasTerminalPrefix
          ? `${terminalName}A0M${macMiniNum}`
          : `A0M${macMiniNum}`
      }
    }

    if (phoneMatch) {
      const phoneNum = phoneMatch[1]
      if (macMiniMatch) {
        phoneName = `${macMiniName}P${phoneNum}`
      } else {
        // Direct phone (A0M0P1 pattern)
        phoneName = hasTerminalPrefix
          ? `${terminalName}A0M0P${phoneNum}`
          : `A0M0P${phoneNum}`
      }
    }

    // Determine entity type (highest level entity found)
    let entityType: ParsedEntity['entityType']
    if (phoneMatch) entityType = 'physical-phone'
    else if (macMiniMatch) entityType = 'mac-mini'
    else if (ansibleMatch) entityType = 'ansible'
    else entityType = 'terminal'

    // Check if has ansible (not A0 pattern)
    const hasAnsible = ansibleMatch ? ansibleMatch[1] !== '0' : false

    return {
      terminalName,
      ansibleName: hasAnsible ? ansibleName : undefined,
      macMiniName,
      phoneName,
      entityType,
      hasAnsible,
    }
  }
}
